import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import type { SelectOption } from '@/components';

import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';

import { bookingEventQueries } from '@/queries';

import { BookingDialogBody } from '../BookingDialogBody';
import { MissedCommentDialog } from '../MissedCommentDialog';
import { CancelBookingDialog } from '../ReservationDetailDialog';

import { AddRegistrationForm } from './AddRegistrationForm';
import { EditRegistrationForm } from './EditRegistrationForm';
import { EventSummary } from './EventSummary';
import { MoveRegistrationForm } from './MoveRegistrationForm';
import { RegistrationAttachForm } from './RegistrationAttachForm';
import { RegistrationsFooter } from './RegistrationsFooter';
import { RegistrationsHeader } from './RegistrationsHeader';
import { RegistrationsList } from './RegistrationsList';
import { useRegistrationActions } from './useRegistrationActions';
import { useRegistrationsFilters } from './useRegistrationsFilters';
import { registrationsExportUrl } from './utils';
import { WavePicker } from './WavePicker';

type EventRegistrationsMode =
  | { kind: 'view' }
  | { kind: 'add' }
  | { kind: 'attach'; registrationId: number }
  | { kind: 'edit'; registrationId: number }
  | { kind: 'move'; registrationId: number };

const VIEW_MODE: EventRegistrationsMode = { kind: 'view' };

export type EventRegistrationsProps = {
  eventId: number;
  eventsUrl: string;
  reservationsUrl: string;
  clientsSearchUrl: string;
  clientsUrl: string;
  newClientsUrl: string;
  cashRegistersUrl: string;
  refundReasons: SelectOption[];
  eventPageUrl?: string;
  initialWaveId?: number | null;
  onClose: () => void;
  onChanged: () => void;
};

export const EventRegistrations = (props: EventRegistrationsProps) => {
  const {
    eventId,
    eventsUrl,
    reservationsUrl,
    clientsSearchUrl,
    clientsUrl,
    newClientsUrl,
    cashRegistersUrl,
    refundReasons,
    eventPageUrl,
    initialWaveId = null,
    onChanged,
  } = props;
  const { t } = useTranslation();

  const [mode, setMode] = useState<EventRegistrationsMode>(VIEW_MODE);
  const onBack = () => setMode(VIEW_MODE);

  const {
    data: event,
    isLoading,
    refetch,
  } = useQuery({
    ...bookingEventQueries.detail({ url: eventsUrl, id: eventId }),
    refetchOnWindowFocus: true,
  });

  const filters = useRegistrationsFilters({ event, initialWaveId });

  const onEventChange = () => {
    refetch();
    onChanged();
  };

  const actions = useRegistrationActions({
    reservationsUrl,
    onChange: onEventChange,
  });

  const onAttend = (registrationId: number) =>
    setMode({ kind: 'attach', registrationId });

  const onEdit = (registrationId: number) =>
    setMode({ kind: 'edit', registrationId });

  const onMove = (registrationId: number) =>
    setMode({ kind: 'move', registrationId });

  if (event && mode.kind === 'add') {
    return (
      <AddRegistrationForm
        event={event}
        eventsUrl={eventsUrl}
        clientsSearchUrl={clientsSearchUrl}
        waveId={event.split_into_waves ? filters.selectedWaveId : null}
        onClose={onBack}
        onSuccess={onEventChange}
      />
    );
  }

  const isRegistrationMode = mode.kind === 'edit' || mode.kind === 'move';

  const modeRegistration = isRegistrationMode
    ? event?.registrations.find(
        (registration) => registration.id === mode.registrationId,
      )
    : undefined;

  if (event && isRegistrationMode && !modeRegistration) {
    setMode(VIEW_MODE);
  }

  if (event && modeRegistration && mode.kind === 'edit') {
    return (
      <EditRegistrationForm
        event={event}
        registration={modeRegistration}
        eventsUrl={eventsUrl}
        onClose={onBack}
        onSuccess={onEventChange}
      />
    );
  }

  if (event && modeRegistration && mode.kind === 'move') {
    return (
      <MoveRegistrationForm
        event={event}
        registration={modeRegistration}
        eventsUrl={eventsUrl}
        onClose={onBack}
        onSuccess={onEventChange}
      />
    );
  }

  if (mode.kind === 'attach') {
    return (
      <RegistrationAttachForm
        registrationId={mode.registrationId}
        reservationsUrl={reservationsUrl}
        clientsSearchUrl={clientsSearchUrl}
        clientsUrl={clientsUrl}
        newClientsUrl={newClientsUrl}
        onClose={onBack}
        onSuccess={onEventChange}
      />
    );
  }

  return (
    <>
      <BookingDialogBody>
        {isLoading && <Skeleton className="h-64 w-full" />}

        {!isLoading && event && (
          <>
            <EventSummary event={event} />

            {event.split_into_waves && (
              <>
                <Separator className="my-2" />

                <WavePicker
                  waves={event.waves}
                  selectedWaveId={filters.selectedWaveId}
                  onWaveSelect={filters.onWaveSelect}
                />
              </>
            )}

            <Separator className="my-2" />

            <RegistrationsHeader
              searchQuery={filters.searchQuery}
              onSearchChange={filters.onSearchChange}
            />

            {filters.isNothingFound ? (
              <p className="text-muted-foreground text-sm">
                {t('No clients found')}
              </p>
            ) : (
              <RegistrationsList
                registrations={filters.registrations}
                waveNames={filters.waveNames}
                isPending={actions.isPending}
                onAttend={onAttend}
                onMarkMissed={actions.missedComment.openMark}
                onMissedCommentEdit={actions.missedComment.openEdit}
                onCancel={actions.onCancelOpen}
                onEdit={onEdit}
                onMove={onMove}
              />
            )}
          </>
        )}
      </BookingDialogBody>

      <RegistrationsFooter
        event={event}
        eventPageUrl={eventPageUrl}
        exportUrl={
          event &&
          registrationsExportUrl(eventsUrl, event, filters.selectedWaveId)
        }
        isWaveUnpicked={filters.isWaveUnpicked}
        isAddDisabled={filters.isAddDisabled}
        onAdd={() => setMode({ kind: 'add' })}
      />

      {actions.missedComment.dialogProps && (
        <MissedCommentDialog {...actions.missedComment.dialogProps} />
      )}

      {!!actions.cancelledRegistrationId && (
        <CancelBookingDialog
          isOpen
          cashRegistersUrl={cashRegistersUrl}
          refundReasons={refundReasons}
          isPending={actions.isCancelPending}
          title={t('Cancel this registration?')}
          description={t(
            'The place is freed. A purchase made for this registration is refunded.',
          )}
          onOpenChange={(isOpen) => !isOpen && actions.onCancelClose()}
          onConfirm={actions.onCancelConfirm}
        />
      )}
    </>
  );
};
